<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'

import BetaSubscriptionBillCard from '@/components/beta-dashboard/molecules/BetaSubscriptionBillCard.vue'
import BetaSubscriptionProductCard from '@/components/beta-dashboard/molecules/BetaSubscriptionProductCard.vue'
import BetaPanelHeading from '@/components/beta-dashboard/molecules/BetaPanelHeading.vue'
import { useApplicationStore } from '@/stores/application'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import { useSiteStore } from '@/stores/site'
import { buildCheckoutUrl } from '@/utils/checkout-url'

const applicationStore = useApplicationStore()
const betaStore = useBetaDashboardStore()
const siteStore = useSiteStore()
const productFilter = ref('all')

const productNames = computed(() => (
    [...new Set(betaStore.subscriptions.contracts.map((contract) => contract.name))]
        .filter(Boolean)
))

const filteredBills = computed(() => (
    productFilter.value === 'all'
        ? betaStore.subscriptions.bills
        : betaStore.subscriptions.bills.filter((bill) => bill.product === productFilter.value)
))

const checkoutUrl = computed(() => (
    buildCheckoutUrl(applicationStore.wizardUrl, siteStore.contract?.token)
))

const openCheckout = () => {
    if (!checkoutUrl.value) return
    window.location.href = checkoutUrl.value
}

const loadSubscriptions = async () => {
    await betaStore.loadSubscriptions()

    if (!betaStore.subscriptionsError && betaStore.subscriptions.contracts.length === 0) {
        openCheckout()
    }
}

onBeforeMount(loadSubscriptions)
</script>

<template>
    <div class="beta-subscriptions">
        <BetaPanelHeading title="Assinaturas">
            <template v-if="betaStore.subscriptions.payMethod" #actions>
                <span class="beta-subscriptions__payment">
                    Pagamento: <strong>{{ betaStore.subscriptions.payMethod }}</strong>
                </span>
            </template>
        </BetaPanelHeading>

        <div v-if="betaStore.loadingSubscriptions" class="beta-subscriptions__state">
            Carregando assinaturas…
        </div>

        <div v-else-if="betaStore.subscriptionsError" class="beta-subscriptions__state is-error">
            <span>{{ betaStore.subscriptionsError }}</span>
            <button type="button" @click="loadSubscriptions">Tentar novamente</button>
        </div>

        <div
            v-else-if="!betaStore.subscriptions.contracts.length"
            class="beta-subscriptions__state"
        >
            <strong>Nenhuma assinatura encontrada</strong>
            <span>Você será direcionado ao checkout para escolher um produto.</span>
            <button v-if="checkoutUrl" type="button" @click="openCheckout">Ir para o checkout</button>
        </div>

        <template v-else>
            <section class="beta-subscriptions__section" aria-labelledby="beta-products-title">
                <div class="beta-subscriptions__section-heading">
                    <div>
                        <h2 id="beta-products-title">Seus produtos</h2>
                        <span>
                            {{ betaStore.subscriptions.contracts.length }}
                            {{ betaStore.subscriptions.contracts.length === 1 ? 'produto contratado' : 'produtos contratados' }}
                        </span>
                    </div>
                </div>

                <div class="beta-subscriptions__products">
                    <BetaSubscriptionProductCard
                        v-for="contract in betaStore.subscriptions.contracts"
                        :key="contract.id"
                        :contract="contract"
                    />
                </div>
            </section>

            <section class="beta-subscriptions__section" aria-labelledby="beta-bills-title">
                <div class="beta-subscriptions__section-heading">
                    <div>
                        <h2 id="beta-bills-title">Faturas</h2>
                        <span>
                            {{ filteredBills.length }}
                            {{ filteredBills.length === 1 ? 'fatura encontrada' : 'faturas encontradas' }}
                        </span>
                    </div>

                    <label v-if="productNames.length > 1" class="beta-subscriptions__filter">
                        <span>Filtrar por produto</span>
                        <select v-model="productFilter">
                            <option value="all">Todos</option>
                            <option v-for="product in productNames" :key="product" :value="product">
                                {{ product }}
                            </option>
                        </select>
                    </label>
                </div>

                <div v-if="filteredBills.length" class="beta-subscriptions__bills">
                    <BetaSubscriptionBillCard
                        v-for="bill in filteredBills"
                        :key="bill.id"
                        :bill="bill"
                    />
                </div>

                <div v-else class="beta-subscriptions__empty-bills">
                    Nenhuma fatura encontrada para este produto.
                </div>
            </section>
        </template>
    </div>
</template>

<style scoped>
.beta-subscriptions {
    display: grid;
    gap: 20px;
}

.beta-subscriptions__payment {
    flex: none;
    border-radius: 10px;
    background: #f0f4f8;
    padding: 8px 10px;
    color: #77879b;
    font-size: 8px;
}

.beta-subscriptions__payment strong {
    color: #354d68;
}

.beta-subscriptions__section {
    display: grid;
    gap: 11px;
}

.beta-subscriptions__section-heading {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 14px;
}

.beta-subscriptions__section-heading h2 {
    color: #2c4561;
    font-size: 14px;
    font-weight: 850;
}

.beta-subscriptions__section-heading > div > span {
    display: block;
    margin-top: 2px;
    color: #8996a7;
    font-size: 8px;
}

.beta-subscriptions__products {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.beta-subscriptions__bills {
    display: grid;
    gap: 8px;
}

.beta-subscriptions__filter {
    display: grid;
    gap: 4px;
}

.beta-subscriptions__filter > span {
    color: #8794a6;
    font-size: 7px;
    font-weight: 800;
    text-transform: uppercase;
}

.beta-subscriptions__filter select {
    min-width: 160px;
    height: 34px;
    border: 1px solid #dce4ed;
    border-radius: 10px;
    background: #fff;
    padding: 0 10px;
    color: #40566f;
    font-size: 9px;
    font-weight: 750;
}

.beta-subscriptions__state,
.beta-subscriptions__empty-bills {
    display: grid;
    min-height: 180px;
    place-content: center;
    justify-items: center;
    gap: 7px;
    border: 1px dashed #dbe3ed;
    border-radius: 18px;
    background: #f9fbfd;
    padding: 24px;
    color: #7e8da2;
    font-size: 9px;
    text-align: center;
}

.beta-subscriptions__state strong {
    color: #304863;
    font-size: 12px;
}

.beta-subscriptions__state.is-error {
    color: #a34e4e;
}

.beta-subscriptions__state button {
    min-height: 34px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 12px;
    color: #fff;
    font-size: 8px;
    font-weight: 850;
}

.beta-subscriptions__empty-bills {
    min-height: 110px;
}

@media (max-width: 820px) {
    .beta-subscriptions__products {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 560px) {
    .beta-subscriptions__heading,
    .beta-subscriptions__section-heading {
        align-items: stretch;
        flex-direction: column;
    }

    .beta-subscriptions__filter select {
        width: 100%;
    }
}
</style>
